import { IonButton, IonIcon, IonSkeletonText } from '@ionic/react';
import { CapacitorCrisp } from '@capgo/capacitor-crisp';
import { useTranslation } from 'react-i18next';
import OCAuth from '../client/OCAuth';
import styles from './StateContainer.module.css';

type StateContainerBadge = 'add' | 'alert' | 'lock';

interface StateContainerProps {
  title: string;
  subtitle?: string;
  /** Small circular icon floating over the logo tile's bottom-right corner. */
  badge?: StateContainerBadge;
  /** 'brand' shows the blue logo tile; 'muted' greys it out for failure states. */
  variant?: 'brand' | 'muted';
  /** Technical detail (e.g. a raw error message) shown in a monospace block. */
  detail?: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
}

const BadgeIcon = ({ badge }: { badge: StateContainerBadge }) => {
  switch (badge) {
    case 'add':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case 'alert':
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="var(--ion-color-danger)" aria-hidden="true">
          <path d="M12 3 22 20H2Z" />
          <rect fill="var(--ion-color-danger-contrast, #fff)" width="2" height="5.5" rx="1" x="11" y="9.5" />
          <circle fill="var(--ion-color-danger-contrast, #fff)" cx="12" cy="17.4" r="1.2" />
        </svg>
      );
    case 'lock':
      return (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <rect fill="currentColor" stroke="none" width="14" height="9.5" rx="2.5" x="5" y="10.5" />
          <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
        </svg>
      );
  }
};

const StateContainer: React.FC<StateContainerProps> = ({
  title, subtitle, badge, variant = 'brand', detail,
  actionLabel, onAction, secondaryActionLabel, onSecondaryAction
}) => {
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={`${styles.tile} ${variant === 'muted' ? styles.tileMuted : ''}`}>
          <IonIcon className={styles.logo} src="/assets/logo/logo-mark.svg" aria-hidden="true" />
          {badge && <div className={styles.badge}><BadgeIcon badge={badge} /></div>}
        </div>
        <strong className={styles.title}>{title}</strong>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        {detail && <code className={styles.detail}>{detail}</code>}
        {actionLabel && (
          <IonButton className={styles.action} shape="round" onClick={onAction}>
            {actionLabel}
          </IonButton>
        )}
        {secondaryActionLabel && (
          <button type="button" className={styles.secondaryAction} onClick={onSecondaryAction}>
            {secondaryActionLabel}
          </button>
        )}
      </div>
    </div>
  );
};

/** Initial-load state: pulsing brand tile with shimmering text placeholders. */
export const LoadingStateContainer: React.FC = () => (
  <div className={styles.container} aria-busy="true">
    <div className={styles.content}>
      <div className={`${styles.tile} ${styles.tileLoading}`}>
        <IonIcon className={styles.logo} src="/assets/logo/logo-mark.svg" aria-hidden="true" />
      </div>
      <IonSkeletonText animated className={styles.skeletonTitle} />
      <IonSkeletonText animated className={styles.skeletonSubtitle} />
    </div>
  </div>
);

/** 401 state: brand tile with a lock badge and login/signup actions. */
export const NotLoggedInStateContainer: React.FC<{ subtitle: string }> = ({ subtitle }) => {
  const { t } = useTranslation('components');

  return (
    <StateContainer
      badge="lock"
      title={t('state_container.not_logged_in_title')}
      subtitle={subtitle}
      actionLabel={t('state_container.log_in')}
      onAction={() => OCAuth.login()}
      secondaryActionLabel={t('state_container.create_account')}
      onSecondaryAction={() => OCAuth.login({ screenHint: 'signup' })}
    />
  );
};

/** Load-failure state: muted tile with an alert badge, error detail, retry and support actions. */
export const LoadFailureStateContainer: React.FC<{
  subtitle: string;
  error?: Error;
  onRetry?: () => void;
}> = ({ subtitle, error, onRetry }) => {
  const { t } = useTranslation('components');

  return (
    <StateContainer
      badge="alert"
      variant="muted"
      title={t('state_container.error_title')}
      subtitle={subtitle}
      detail={error?.message}
      actionLabel={onRetry ? t('state_container.try_again') : undefined}
      onAction={onRetry}
      secondaryActionLabel={t('state_container.contact_support')}
      onSecondaryAction={() => CapacitorCrisp.openMessenger()}
    />
  );
};

export default StateContainer;
